import { memo, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import type { FormFieldData } from '@/services/api-types';
import type { FormFieldBaseProps } from './field-base';
import { TextField } from './TextField';
import { TextAreaField } from './TextAreaField';
import { DateField } from './DateField';
import { PhoneField } from './PhoneField';
import { EmailField } from './EmailField';
import { NumberField } from './NumberField';
import { RadioField } from './RadioField';
import { SelectField } from './SelectField';
import { CheckboxGroupField } from './CheckboxGroupField';

export type TableRow = Record<string, unknown>;

export interface TableFieldProps extends FormFieldBaseProps<TableRow[] | null | undefined> {}

/**
 * TABLE error display. The renderer's `errors` map is keyed by top-level
 * field key, so a cell-coercion error fires against the table itself rather
 * than an individual cell. We render the message under the table.
 */

/**
 * Editable grid. Each row is a Record<columnKey, cellValue>.
 * Columns come from field.columns: FormFieldData[] and are scalar field types
 * (no nested tables / computed — enforced by TABLE_COLUMN_FORM_FIELD_TYPES).
 *
 * Read-only renders a plain HTML table; edit mode renders the same column's
 * regular field component per cell, with a hidden label (we place the label
 * in the table header instead).
 */
export const TableField = memo(function TableField({
  field,
  value,
  onChange,
  readOnly = false,
  error,
}: TableFieldProps) {
  const { theme } = useTheme();
  const columns = field.columns ?? [];
  const predefinedRows = field.predefinedRows;
  const isFixedRows = Array.isArray(predefinedRows) && predefinedRows.length > 0;

  // For fixed-rows tables, the value is a map keyed by row.id of column-value
  // objects. For free-form tables, value is an array of column-value objects.
  // We coerce both cases to a positional row list for rendering.
  const rows: TableRow[] = isFixedRows
    ? predefinedRows!.map((r) => {
        const stored = (value as Record<string, TableRow> | null | undefined)?.[r.id];
        return stored && typeof stored === 'object' ? stored : {};
      })
    : Array.isArray(value)
      ? value
      : [];

  const handleAddRow = useCallback(() => {
    if (readOnly || isFixedRows) return;
    const next = (Array.isArray(value) ? value : []).concat([{}]);
    onChange(next);
  }, [value, onChange, readOnly, isFixedRows]);

  const handleRemoveRow = useCallback(
    (rowIdx: number) => {
      if (readOnly || isFixedRows) return;
      const arr = Array.isArray(value) ? value : [];
      const next = arr.slice(0, rowIdx).concat(arr.slice(rowIdx + 1));
      onChange(next);
    },
    [value, onChange, readOnly, isFixedRows],
  );

  const handleCellChange = useCallback(
    (rowIdx: number, columnKey: string, cellValue: unknown) => {
      if (readOnly) return;
      if (isFixedRows) {
        const map = (value && typeof value === 'object' && !Array.isArray(value))
          ? (value as Record<string, TableRow>)
          : {};
        const rowId = predefinedRows![rowIdx].id;
        const nextMap: Record<string, TableRow> = {
          ...map,
          [rowId]: { ...(map[rowId] ?? {}), [columnKey]: cellValue },
        };
        onChange(nextMap as unknown as TableRow[]);
      } else {
        const arr = Array.isArray(value) ? value : [];
        const next = arr.slice();
        const updatedRow: TableRow = { ...(next[rowIdx] ?? {}), [columnKey]: cellValue };
        next[rowIdx] = updatedRow;
        onChange(next);
      }
    },
    [value, onChange, readOnly, isFixedRows, predefinedRows],
  );

  // Compact column types whose content has a small natural width — letting them
  // shrink-to-fit (`width: 1%; white-space: nowrap` in CSS) means the wide
  // content columns (text/textarea/etc.) absorb the remaining space.
  const isNarrow = (col: FormFieldData): boolean =>
    col.type === 'radio' || col.type === 'date' || col.type === 'number' || col.type === 'computed';
  const cellClass = (col: FormFieldData): string => {
    let cls = 'table-field-cell';
    if (col.type === 'radio') cls += ' table-field-cell--center';
    if (isNarrow(col)) cls += ' table-field-cell--narrow';
    return cls;
  };
  const headerClass = (col: FormFieldData): string => {
    let cls = '';
    if (col.type === 'radio') cls += 'table-field-cell--center';
    if (isNarrow(col)) cls += (cls ? ' ' : '') + 'table-field-cell--narrow';
    return cls;
  };

  const errorId = error ? `${field.key}__error` : undefined;

  if (readOnly) {
    return (
      <div className={'form-group table-field' + (theme ? ' ' + theme : '') + (error ? ' has-error' : '')}>
        <label className={'field-label' + (field.required ? ' required' : '')}>
          {field.label}
        </label>
        <table className={'table-field-table table-field-table--readonly' + (error ? ' table-field-table--error' : '')}>
          <thead>
            <tr>
              {isFixedRows && <th aria-label="Row" />}
              {columns.map((col) => (
                <th key={col.key} className={headerClass(col)}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIdx) => (
              <tr key={isFixedRows ? predefinedRows![rowIdx].id : rowIdx}>
                {isFixedRows && (
                  <td className="table-field-row-label">{predefinedRows![rowIdx].label}</td>
                )}
                {columns.map((col) => (
                  <td key={col.key} className={cellClass(col)}>{formatReadOnlyCell(col, row[col.key])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {error ? <div id={errorId} className="form-field-error" role="alert">{error}</div> : null}
      </div>
    );
  }

  return (
    <div className={'form-group table-field' + (theme ? ' ' + theme : '') + (error ? ' has-error' : '')}>
      <label className={'field-label' + (field.required ? ' required' : '')}>
        {field.label}
      </label>
      <table className={'table-field-table' + (error ? ' table-field-table--error' : '')}>
        <thead>
          <tr>
            {isFixedRows && <th aria-label="Row" />}
            {columns.map((col) => (
              <th key={col.key} className={headerClass(col)}>
                {col.label}
                {col.required ? ' *' : ''}
              </th>
            ))}
            {!isFixedRows && <th className="table-field-actions-col" aria-label="Actions" />}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIdx) => (
            <tr key={isFixedRows ? predefinedRows![rowIdx].id : rowIdx}>
              {isFixedRows && (
                <td className="table-field-row-label">{predefinedRows![rowIdx].label}</td>
              )}
              {columns.map((col) => (
                <td key={col.key} className={cellClass(col)}>
                  <TableCell
                    column={col}
                    cellValue={row[col.key]}
                    rowIdx={rowIdx}
                    rowKey={isFixedRows ? predefinedRows![rowIdx].id : String(rowIdx)}
                    onChange={(next) => handleCellChange(rowIdx, col.key, next)}
                  />
                </td>
              ))}
              {!isFixedRows && (
                <td className="table-field-actions-cell">
                  <button
                    type="button"
                    className="kstc-btn kstc-btn--secondary table-field-remove-row"
                    onClick={() => handleRemoveRow(rowIdx)}
                  >
                    Remove
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {!isFixedRows && (
        <div className="table-field-footer">
          <button
            type="button"
            className="kstc-btn kstc-btn--primary table-field-add-row"
            onClick={handleAddRow}
          >
            Add row
          </button>
        </div>
      )}
      {error ? <div id={errorId} className="form-field-error" role="alert">{error}</div> : null}
    </div>
  );
});

interface TableCellProps {
  column: FormFieldData;
  cellValue: unknown;
  /** Row index in the current render (used to scope inputs that need uniqueness). */
  rowIdx: number;
  /** Stable row identity (predefined row id, or stringified index for free-form). */
  rowKey: string;
  onChange: (next: unknown) => void;
}

/**
 * Renders a single cell's editor. Delegates to the per-type field component.
 * We hide the per-cell label by using a zero-width field label — the column
 * label appears in the table header instead.
 *
 * For RADIO columns we pass a per-row HTML `name` so the browser scopes each
 * row's radio group independently. Without this, all rows share `name=col.key`
 * and clicking Yes on one row unchecks Yes on every other row.
 */
function TableCell({ column, cellValue, rowIdx: _rowIdx, rowKey, onChange }: TableCellProps) {
  // Strip the label so the inner component shows a blank label slot.
  // We keep `required` so the `*` stays consistent, but in practice the
  // per-cell required indicator is shown in the header.
  const cellField: FormFieldData = { ...column, label: '' };
  const radioName = `${column.key}__row_${rowKey}`;

  switch (column.type) {
    case 'text':
      return <TextField field={cellField} value={(cellValue as string | null | undefined) ?? ''} onChange={onChange as (v: string | null | undefined) => void} />;
    case 'textarea':
      return <TextAreaField field={cellField} value={(cellValue as string | null | undefined) ?? ''} onChange={onChange as (v: string | null | undefined) => void} />;
    case 'date':
      return <DateField field={cellField} value={(cellValue as string | null | undefined) ?? ''} onChange={onChange as (v: string | null | undefined) => void} />;
    case 'phone':
      return <PhoneField field={cellField} value={(cellValue as string | null | undefined) ?? ''} onChange={onChange as (v: string | null | undefined) => void} />;
    case 'email':
      return <EmailField field={cellField} value={(cellValue as string | null | undefined) ?? ''} onChange={onChange as (v: string | null | undefined) => void} />;
    case 'number':
      return <NumberField field={cellField} value={(cellValue as number | null | undefined) ?? null} onChange={onChange as (v: number | null | undefined) => void} />;
    case 'radio':
      return <RadioField field={cellField} value={(cellValue as string | null | undefined) ?? ''} onChange={onChange as (v: string | null | undefined) => void} name={radioName} />;
    case 'select':
      return <SelectField field={cellField} value={(cellValue as string | null | undefined) ?? ''} onChange={onChange as (v: string | null | undefined) => void} />;
    case 'checkboxGroup':
      return <CheckboxGroupField field={cellField} value={(cellValue as string[] | null | undefined) ?? []} onChange={onChange as (v: string[] | null | undefined) => void} />;
    default:
      // 'table' and 'computed' are not allowed as table columns per
      // TABLE_COLUMN_FORM_FIELD_TYPES; render a noop fallback just in case.
      return <span className="table-field-cell-unsupported">(unsupported cell type)</span>;
  }
}

/**
 * Read-only cell formatter. For option-based columns (radio/select/checkboxGroup),
 * map stored values back to their human labels so the table reads the same way
 * the editor does — "Yes" instead of "Y", joined with commas for multi-select.
 */
function formatReadOnlyCell(column: FormFieldData, cellValue: unknown): string {
  if (cellValue == null || cellValue === '') return '';
  const options = Array.isArray(column.options) ? column.options : [];
  const labelFor = (val: unknown): string => {
    const opt = options.find((o) => o.value === val);
    return opt ? opt.label : String(val);
  };
  if (column.type === 'radio' || column.type === 'select') {
    return labelFor(cellValue);
  }
  if (column.type === 'checkboxGroup' && Array.isArray(cellValue)) {
    return cellValue.map(labelFor).join(', ');
  }
  if (Array.isArray(cellValue)) return cellValue.join(', ');
  return String(cellValue);
}
